import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, Building2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormDrawer } from '@/components/ui/form-drawer';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';

interface Account {
  id: string;
  name: string;
  code?: string;
  accountNumber?: string;
  externalId?: string;
  status: string;
  industry?: string;
  website?: string;
  creditHold?: boolean;
  notes?: string;
}

interface Location {
  id: string;
  name: string;
  code?: string;
  isPrimary?: boolean;
  account: Account;
}

interface AccountFormData {
  name: string;
  code: string;
  accountNumber: string;
  externalId: string;
  locationName: string;
  industry: string;
  phone: string;
  website: string;
  status: string;
  notes: string;
  parentAccountId?: string;
}

const industries = [
  'Food Service',
  'Retail',
  'Healthcare',
  'Education',
  'Hospitality',
  'Manufacturing',
  'Wholesale',
  'Other',
];

export function AccountsPage() {
  const { sessionId } = useAuth();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  
  const [newAccountDrawerOpen, setNewAccountDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  const [formData, setFormData] = useState<AccountFormData>({
    name: '',
    code: '',
    accountNumber: '',
    externalId: '',
    locationName: 'Main',
    industry: '',
    phone: '',
    website: '',
    status: 'active',
    notes: '',
    parentAccountId: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fetch locations (accounts with their primary location)
  const { data: locations, isLoading } = useQuery({
    queryKey: ['locations', sessionId],
    queryFn: async () => {
      const result = await api.get<{ accounts: Account[] }>('/accounts', sessionId);
      // Transform accounts to location format for display
      return result.accounts.map(account => ({
        id: account.id,
        name: 'Main',
        code: account.code,
        isPrimary: true,
        account: account,
      })) as Location[];
    },
    enabled: !!sessionId,
  });

  // Filter locations
  const filteredLocations = (locations || []).filter(loc => {
    if (searchValue) {
      const q = searchValue.toLowerCase();
      const matches =
        loc.account.name.toLowerCase().includes(q) ||
        loc.name.toLowerCase().includes(q) ||
        loc.code?.toLowerCase().includes(q) ||
        loc.account.accountNumber?.toLowerCase().includes(q) ||
        loc.account.externalId?.toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (statusFilter !== 'all' && loc.account.status !== statusFilter) return false;
    return true;
  });

  // Create account mutation
  const createMutation = useMutation({
    mutationFn: async (data: AccountFormData) => {
      const { locationName, ...accountData } = data;
      return api.post('/accounts', {
        ...accountData,
        locationName,
      }, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setNewAccountDrawerOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      const msg = error.message || 'Failed to create account';
      if (/external id/i.test(msg)) {
        setFormErrors({ externalId: msg });
      } else if (/reference number/i.test(msg)) {
        setFormErrors({ accountNumber: msg });
      } else if (/code/i.test(msg)) {
        setFormErrors({ code: msg });
      } else {
        setFormErrors({ general: msg });
      }
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      accountNumber: '',
      externalId: '',
      locationName: 'Main',
      industry: '',
      phone: '',
      website: '',
      status: 'active',
      notes: '',
      parentAccountId: '',
    });
    setFormErrors({});
  };

  const handleDrawerClose = (open: boolean) => {
    setNewAccountDrawerOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Account name is required';
    }
    
    if (!formData.locationName.trim()) {
      errors.locationName = 'Location name is required';
    }
    
    if (formData.code && formData.code.length > 16) {
      errors.code = 'Code must be 16 characters or less';
    }
    
    if (formData.code && !/^[A-Z0-9-]*$/.test(formData.code)) {
      errors.code = 'Use uppercase letters, digits, or hyphens';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      createMutation.mutate(formData);
    }
  };

  const handleRowClick = (accountId: string) => {
    setLocation(`/crm/accounts/${accountId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your customer accounts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setNewAccountDrawerOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Account
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search accounts, refs, or external IDs..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">
              Loading accounts...
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No accounts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Industry
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLocations.map((loc) => (
                    <tr
                      key={loc.id}
                      onClick={() => handleRowClick(loc.account.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {loc.account.name}
                          </span>
                          {loc.account.creditHold && (
                            <Badge variant="destructive" className="text-xs">
                              Hold
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {loc.account.code || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {loc.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          loc.account.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : loc.account.status === 'on_hold'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {loc.account.status === 'on_hold' ? 'On Hold' : loc.account.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                        {loc.account.industry || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Account Drawer */}
      <FormDrawer
        open={newAccountDrawerOpen}
        onOpenChange={handleDrawerClose}
        title="New Account"
        description="Create a new customer account"
        formId="create-crm-account"
        isPending={createMutation.isPending}
        submitLabel="Create Account"
        size="md"
        data-testid="drawer-new-crm-account"
      >
        <form id="create-crm-account" onSubmit={handleSubmit} className="space-y-6">
          {/* Account Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Account Name *</Label>
            <Input
              id="name"
              variant="flat"
              placeholder="Enter account name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={formErrors.name ? 'border-destructive' : ''}
            />
            {formErrors.name && (
              <p className="text-sm text-destructive">{formErrors.name}</p>
            )}
          </div>

          {/* Code */}
          <div className="space-y-2">
            <Label htmlFor="code">Code *</Label>
            <Input
              id="code"
              variant="flat"
              placeholder="ACM"
              maxLength={16}
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              className={formErrors.code ? 'border-destructive' : ''}
            />
            {formErrors.code && (
              <p className="text-sm text-destructive">{formErrors.code}</p>
            )}
          </div>

          {/* Reference Number */}
          <div className="space-y-2">
            <Label htmlFor="accountNumber">Reference Number</Label>
            <Input
              id="accountNumber"
              variant="flat"
              placeholder="ERP / legacy ID (optional)"
              value={formData.accountNumber}
              onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
              className={formErrors.accountNumber ? 'border-destructive' : ''}
            />
            {formErrors.accountNumber && (
              <p className="text-sm text-destructive">{formErrors.accountNumber}</p>
            )}
          </div>

          {/* Integration (advanced) - Collapsible */}
          <details className="rounded-md border border-border bg-muted/40 px-3 py-2 group">
            <summary className="text-sm font-medium cursor-pointer select-none flex items-center justify-between">
              <span>Integration (advanced)</span>
              <span className="text-xs text-muted-foreground group-open:hidden">Show</span>
            </summary>
            <div className="pt-3">
              <div className="space-y-2">
                <Label htmlFor="externalId">External ID</Label>
                <Input
                  id="externalId"
                  variant="flat"
                  placeholder="e.g. qbo:12345"
                  value={formData.externalId}
                  onChange={(e) => setFormData({ ...formData, externalId: e.target.value })}
                  className={formErrors.externalId ? 'border-destructive' : ''}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Usually set automatically by a connected system. Leave blank if you're not sure.
                </p>
                {formErrors.externalId && (
                  <p className="text-sm text-destructive">{formErrors.externalId}</p>
                )}
              </div>
            </div>
          </details>

          {/* Location Name */}
          <div className="space-y-2">
            <Label htmlFor="locationName">Location Name *</Label>
            <Input
              id="locationName"
              variant="flat"
              placeholder="e.g. Main, HQ, Warehouse"
              value={formData.locationName}
              onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
              className={formErrors.locationName ? 'border-destructive' : ''}
            />
            {formErrors.locationName && (
              <p className="text-sm text-destructive">{formErrors.locationName}</p>
            )}
          </div>

          {/* Industry */}
          <div className="space-y-2">
            <Label htmlFor="industry">Industry</Label>
            <Select
              value={formData.industry}
              onValueChange={(value) => setFormData({ ...formData, industry: value })}
            >
              <SelectTrigger id="industry" variant="flat">
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                {industries.map((industry) => (
                  <SelectItem key={industry} value={industry.toLowerCase()}>
                    {industry}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              variant="flat"
              placeholder="https://example.com"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              variant="flat"
              placeholder="Add any additional notes about this account..."
              className="min-h-[100px]"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>

          {/* General Error */}
          {formErrors.general && (
            <div className="p-3 rounded-md border border-destructive/50 bg-destructive/10">
              <p className="text-sm text-destructive">{formErrors.general}</p>
            </div>
          )}
        </form>
      </FormDrawer>
    </div>
  );
}
